import React, { useState, useEffect, useCallback } from 'react';
import { useLandlordStore } from '@/stores/useLandlordStore';
import { Loader2, X, Check, Search, User, Mail, Calendar, DollarSign, Home, AlertCircle, RefreshCw } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { supabase } from '@/config/supabase';
import apiClient from '@/services/apiClient';
import { useToast } from '@/contexts/ToastContext';

// Invitation status types matching mobile app pipeline
export type InvitationStatus = 'pending' | 'accepted' | 'declined' | 'expired' | 'revoked';

export interface TenantInvitation {
    id: string;
    tenant_email: string;
    status: InvitationStatus;
    created_at: string;
    expires_at?: string;
    unit_id: string;
    lease_start_date: string;
    rent_amount: number;
    security_deposit_amount: number;
    utilities_responsibilities?: Record<string, string>;
    house_rules?: string;
    units?: {
        id: string;
        unit_number: string;
    };
    properties?: {
        id: string;
        name: string;
        address?: string;
    };
    landlord?: {
        id: string;
        full_name: string;
    };
    accepted_at?: string;
    declined_at?: string;
}

interface InvitationFormData {
    tenantEmail: string;
    unitId: string;
    leaseStartDate: string;
    rentAmount: number;
    securityDeposit: number;
    houseRules?: string;
    utilitiesResponsibilities?: Record<string, string>;
}

interface TenantInviteModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess?: () => void;
}

const inviteSchema = z.object({
    tenantEmail: z.string().email('Invalid email address'),
    unitId: z.string().min(1, 'Please select a unit'),
    leaseStartDate: z.string().min(1, 'Start date is required'),
    rentAmount: z.number().min(0, 'Rent amount must be positive'),
    securityDeposit: z.number().min(0, 'Security deposit must be positive'),
    houseRules: z.string().optional(),
    utilitiesResponsibilities: z.record(z.string(), z.string()).optional(),
});

type InviteFormData = z.infer<typeof inviteSchema>;

// Step types for multi-step flow
type InviteStep = 'search' | 'details' | 'review' | 'success';

export const TenantInviteModal: React.FC<TenantInviteModalProps> = ({ isOpen, onClose, onSuccess }) => {
    const { properties, fetchUnits, units, inviteTenant, loading, fetchProperties } = useLandlordStore();
    const { success: showToast, error: showError } = useToast();
    
    const [currentStep, setCurrentStep] = useState<InviteStep>('search');
    const [selectedPropertyId, setSelectedPropertyId] = useState<string>('');
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [userSearchResults, setUserSearchResults] = useState<any[]>([]);
    const [isSearching, setIsSearching] = useState(false);
    const [showUserDropdown, setShowUserDropdown] = useState(false);
    const [selectedUser, setSelectedUser] = useState<any | null>(null);
    const [invitationData, setInvitationData] = useState<Partial<InvitationFormData>>({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showCancelConfirm, setShowCancelConfirm] = useState(false);

    const { register, handleSubmit, watch, setValue, formState: { errors }, reset, trigger } = useForm<InviteFormData>({
        resolver: zodResolver(inviteSchema),
        defaultValues: {
            houseRules: "No smoking, No pets without approval, Quiet hours after 10 PM",
            securityDeposit: 0,
            rentAmount: 0,
        }
    });

    const selectedUnitId = watch('unitId');
    const emailValue = watch('tenantEmail');
    const rentAmount = watch('rentAmount');
    const securityDeposit = watch('securityDeposit');

    // Reset state when modal opens/closes
    useEffect(() => {
        if (!isOpen) {
            reset();
            setCurrentStep('search');
            setSelectedPropertyId('');
            setSelectedUser(null);
            setInvitationData({});
            setSuccessMessage(null);
            setUserSearchResults([]);
            setShowUserDropdown(false);
        }
    }, [isOpen, reset]);

    // Fetch properties when modal opens
    useEffect(() => {
        if (isOpen && properties.length === 0) {
            fetchProperties().catch(console.error);
        }
    }, [isOpen, properties.length, fetchProperties]);

    // Fetch units when property changes
    useEffect(() => {
        if (selectedPropertyId) {
            fetchUnits(selectedPropertyId);
        }
    }, [selectedPropertyId, fetchUnits]);

    // Update rent amount when unit changes
    // useEffect(() => {
    //     if (selectedUnitId) {
    //         const unit = units.find(u => u.id === selectedUnitId);
    //         if (unit) {
    //             setValue('rentAmount', unit.rent_amount);
    //             // Set default security deposit as equal to rent
    //             if (!securityDeposit) {
    //                 setValue('securityDeposit', unit.rent_amount);
    //             }
    //         }
    //     }
    // }, [selectedUnitId, units, setValue, securityDeposit]);
useEffect(() => {
    if (selectedUnitId) {
        const unit = units.find(u => u.id === selectedUnitId);
        if (unit) {
            setValue('rentAmount', unit.rent_amount, { shouldValidate: false });
            // Set default security deposit as equal to rent
            if (!securityDeposit) {
                setValue('securityDeposit', unit.rent_amount, { shouldValidate: false });
            }
        }
    }
}, [selectedUnitId, units, setValue, securityDeposit]);
    // User Search Logic
    useEffect(() => {
        const searchUsers = async () => {
            if (!emailValue || emailValue.length < 3) {
                setUserSearchResults([]);
                return;
            }

            setIsSearching(true);
            try {
                // Search profiles by email or name
                const { data, error } = await supabase
                    .from('profiles')
                    .select('email, full_name, avatar_url, id')
                    .or(`email.ilike.%${emailValue}%,full_name.ilike.%${emailValue}%`)
                    .limit(5);

                if (!error && data) {
                    setUserSearchResults(data);
                    setShowUserDropdown(true);
                }
            } catch (error) {
                console.error('Error searching users:', error);
            } finally {
                setIsSearching(false);
            }
        };

        const timeoutId = setTimeout(searchUsers, 500);
        return () => clearTimeout(timeoutId);
    }, [emailValue]);

    const selectUser = (user: any) => {
        setValue('tenantEmail', user.email, { shouldValidate: true });
        setSelectedUser(user);
        setShowUserDropdown(false);
    };

    const handleNextStep = async () => {
        if (currentStep === 'search') {
            // Validate email and move to details
            const isValid = await trigger('tenantEmail');
            if (isValid) {
                setInvitationData(prev => ({ ...prev, tenantEmail: emailValue }));
                setCurrentStep('details');
            }
        } else if (currentStep === 'details') {
            // Validate all fields and move to review
            const isValid = await trigger(['unitId', 'leaseStartDate', 'rentAmount', 'securityDeposit']);
            if (isValid && selectedUnitId && selectedPropertyId) {
                const unit = units.find(u => u.id === selectedUnitId);
                setInvitationData(prev => ({
                    ...prev,
                    unitId: selectedUnitId,
                    leaseStartDate: watch('leaseStartDate'),
                    rentAmount: rentAmount,
                    securityDeposit: securityDeposit,
                    houseRules: watch('houseRules'),
                    unitDetails: unit,
                    propertyId: selectedPropertyId
                }));
                setCurrentStep('review');
            }
        }
    };

    const handlePreviousStep = () => {
        if (currentStep === 'details') {
            setCurrentStep('search');
        } else if (currentStep === 'review') {
            setCurrentStep('details');
        }
    };

    const onSubmit = async (data: InviteFormData) => {
        setIsSubmitting(true);
        try {
            const invitationPayload = {
                tenantEmail: data.tenantEmail,
                unitId: data.unitId,
                leaseStartDate: new Date(data.leaseStartDate).toISOString(),
                rentAmount: data.rentAmount,
                securityDeposit: data.securityDeposit,
                houseRules: data.houseRules,
                utilitiesResponsibilities: data.utilitiesResponsibilities ? JSON.stringify(data.utilitiesResponsibilities) : undefined,
                propertyId: selectedPropertyId
            };

            await apiClient.post('/tenants/invite', invitationPayload);
            
            setSuccessMessage(`Invitation sent to ${data.tenantEmail}`);
            setCurrentStep('success');
            showToast('Invitation sent successfully!');
            
            // Refresh tenants list
            await inviteTenant(invitationPayload);
            
            setTimeout(() => {
                onClose();
                if (onSuccess) onSuccess();
            }, 3000);
        } catch (error: any) {
            console.error('Failed to send invitation:', error);
            const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Failed to send invitation';
            showError(errorMessage);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        if (currentStep !== 'search') {
            setShowCancelConfirm(true);
        } else {
            onClose();
        }
    };

    const confirmClose = () => {
        setShowCancelConfirm(false);
        onClose();
    };

    const getSelectedUnit = () => {
        return units.find(u => u.id === selectedUnitId);
    };

    const getSelectedProperty = () => {
        return properties.find(p => p.id === selectedPropertyId);
    };

    // Render different steps
    const renderStep = () => {
        switch (currentStep) {
            case 'search':
                return renderSearchStep();
            case 'details':
                return renderDetailsStep();
            case 'review':
                return renderReviewStep();
            case 'success':
                return renderSuccessStep();
            default:
                return renderSearchStep();
        }
    };

    const renderSearchStep = () => (
        <div className="space-y-6">
            {/* Tenant Search */}
            <div className="relative group">
                <label className="block tracking-wide text-xs font-semibold text-light-subtle dark:text-dark-subtle uppercase mb-2">
                    Prospective Tenant
                </label>
                <div className="relative">
                    <Search className="absolute left-0 top-1/2 -translate-y-1/2 text-light-subtle dark:text-dark-subtle group-focus-within:text-primary transition-colors" size={20} />
                    <input
                        {...register('tenantEmail')}
                        type="text"
                        autoComplete="off"
                        placeholder="Enter tenant's email address..."
                        className="w-full pl-8 pr-4 py-3 bg-transparent border-0 border-b-2 border-black dark:border-white focus:ring-0 focus:border-primary dark:focus:border-primary outline-none transition-colors text-light-text dark:text-dark-text dark:text-white placeholder-gray-400"
                        onFocus={() => { if (userSearchResults.length > 0) setShowUserDropdown(true); }}
                    />
                    {isSearching && <Loader2 className="absolute right-0 top-1/2 -translate-y-1/2 animate-spin text-primary" size={18} />}
                </div>
                {errors.tenantEmail && <p className="text-xs text-danger dark:text-danger-dark mt-2">{errors.tenantEmail.message}</p>}
                {!errors.tenantEmail && (
                    <p className="text-xs text-light-subtle dark:text-dark-subtle mt-2">
                        The tenant will receive an invitation at this email address
                    </p>
                )}

                {/* Typeahead Dropdown */}
                {showUserDropdown && userSearchResults.length > 0 && (
                    <div className="absolute z-20 w-full mt-2 bg-light-card dark:bg-dark-card rounded-xl shadow-xl border border-light-border dark:border-dark-border max-h-60 overflow-auto py-2">
                        {userSearchResults.map((user) => (
                            <button
                                key={user.email}
                                type="button"
                                onClick={() => selectUser(user)}
                                className="w-full text-left px-5 py-3 hover:bg-light-bg dark:hover:bg-dark-bg flex items-center gap-4 transition-colors"
                            >
                                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary shrink-0 overflow-hidden border border-primary/20">
                                    {user.avatar_url ? (
                                        <img src={user.avatar_url} alt={user.full_name} className="w-full h-full object-cover" />
                                    ) : (
                                        <User size={18} />
                                    )}
                                </div>
                                <div>
                                    <p className="text-sm font-semibold text-light-text dark:text-dark-text">{user.full_name || 'Unknown'}</p>
                                    <p className="text-xs text-light-subtle dark:text-dark-subtle">{user.email}</p>
                                </div>
                                {selectedUser?.email === user.email && (
                                    <Check size={16} className="text-primary ml-auto" />
                                )}
                            </button>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );

    const renderDetailsStep = () => (
        <div className="space-y-6">
            {/* Property & Unit Selection */}
            <div className="grid grid-cols-1 gap-6">
                <div className="group">
                    <label className="block tracking-wide text-xs font-semibold text-light-subtle dark:text-dark-subtle uppercase mb-2">
                        Target Property
                    </label>
                    <select
                        value={selectedPropertyId}
                        onChange={(e) => setSelectedPropertyId(e.target.value)}
                        className="w-full px-0 py-3 bg-transparent border-0 border-b-2 border-black dark:border-white focus:ring-0 focus:border-primary dark:focus:border-primary outline-none transition-colors text-light-text dark:text-dark-text dark:text-white"
                    >
                        <option value="" className="text-light-subtle dark:text-dark-subtle">Select Property...</option>
                        {properties.map(p => (
                            <option key={p.id} value={p.id} className="text-light-text dark:text-dark-text">{p.name}</option>
                        ))}
                    </select>
                </div>

                <div className="group">
                    <label className="block tracking-wide text-xs font-semibold text-light-subtle dark:text-dark-subtle uppercase mb-2">
                        Target Unit
                    </label>
                    <select
                        {...register('unitId')}
                        disabled={!selectedPropertyId}
                        className="w-full px-0 py-3 bg-transparent border-0 border-b-2 border-black dark:border-white focus:ring-0 focus:border-primary dark:focus:border-primary outline-none disabled:opacity-30 disabled:border-light-border dark:border-dark-border transition-colors text-light-text dark:text-dark-text dark:text-white"
                    >
                        <option value="" className="text-light-subtle dark:text-dark-subtle">Select Unit...</option>
                        {units
                            .filter(u => u.status === 'Available')
                            .map(u => (
                                <option key={u.id} value={u.id} className="text-light-text dark:text-dark-text">
                                    {u.unit_number} - KES {u.rent_amount?.toLocaleString()}/month
                                </option>
                            ))}
                    </select>
                    {errors.unitId && <p className="text-xs text-danger dark:text-danger-dark mt-2">{errors.unitId.message}</p>}
                </div>
            </div>

            {/* Lease Details */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="group">
                    <label className="block tracking-wide text-xs font-semibold text-light-subtle dark:text-dark-subtle uppercase mb-2">
                        Lease Start Date
                    </label>
                    <div className="relative">
                        <Calendar className="absolute left-0 top-1/2 -translate-y-1/2 text-light-subtle dark:text-dark-subtle" size={18} />
                        <input
                            {...register('leaseStartDate')}
                            type="date"
                            min={new Date().toISOString().split('T')[0]}
                            className="w-full pl-8 pr-4 py-2 bg-transparent border-0 border-b-2 border-black dark:border-white focus:ring-0 focus:border-primary dark:focus:border-primary outline-none transition-colors text-light-text dark:text-dark-text dark:text-white [color-scheme:light] dark:[color-scheme:dark]"
                        />
                    </div>
                    {errors.leaseStartDate && <p className="text-xs text-danger dark:text-danger-dark mt-2">{errors.leaseStartDate.message}</p>}
                </div>

                <div className="group">
                    <label className="block tracking-wide text-xs font-semibold text-light-subtle dark:text-dark-subtle uppercase mb-2">
                        Monthly Rent (KES)
                    </label>
                    <div className="relative">
                        <DollarSign className="absolute left-0 top-1/2 -translate-y-1/2 text-light-subtle dark:text-dark-subtle" size={18} />
                        <input
                            {...register('rentAmount', { valueAsNumber: true })}
                            type="number"
                            placeholder="0.00"
                            className="w-full pl-8 pr-4 py-2 bg-transparent border-0 border-b-2 border-black dark:border-white focus:ring-0 focus:border-primary dark:focus:border-primary outline-none transition-colors text-light-text dark:text-dark-text dark:text-white placeholder-gray-400"
                        />
                    </div>
                    {errors.rentAmount && <p className="text-xs text-danger dark:text-danger-dark mt-2">{errors.rentAmount.message}</p>}
                </div>
            </div>

            {/* Security Deposit */}
            <div className="group">
                <label className="block tracking-wide text-xs font-semibold text-light-subtle dark:text-dark-subtle uppercase mb-2">
                    Security Deposit (KES)
                </label>
                <div className="relative">
                    <DollarSign className="absolute left-0 top-1/2 -translate-y-1/2 text-light-subtle dark:text-dark-subtle" size={18} />
                    <input
                        {...register('securityDeposit', { valueAsNumber: true })}
                        type="number"
                        placeholder="0.00"
                        className="w-full pl-8 pr-4 py-2 bg-transparent border-0 border-b-2 border-black dark:border-white focus:ring-0 focus:border-primary dark:focus:border-primary outline-none transition-colors text-light-text dark:text-dark-text dark:text-white placeholder-gray-400"
                    />
                </div>
                {errors.securityDeposit && <p className="text-xs text-danger dark:text-danger-dark mt-2">{errors.securityDeposit.message}</p>}
                <p className="text-xs text-light-subtle dark:text-dark-subtle mt-2">
                    Typically equal to one month's rent
                </p>
            </div>

            {/* House Rules */}
            <div className="group">
                <label className="block tracking-wide text-xs font-semibold text-light-subtle dark:text-dark-subtle uppercase mb-2">
                    House Rules (Optional)
                </label>
                <textarea
                    {...register('houseRules')}
                    rows={3}
                    placeholder="Enter any specific house rules..."
                    className="w-full px-4 py-2 bg-transparent border border-light-border dark:border-dark-border rounded-lg focus:ring-0 focus:border-primary dark:focus:border-primary outline-none transition-colors text-light-text dark:text-dark-text dark:text-white placeholder-gray-400 resize-none"
                />
            </div>
        </div>
    );

    const renderReviewStep = () => {
        const unit = getSelectedUnit();
        const property = getSelectedProperty();

        return (
            <div className="space-y-6">
                <div className="bg-light-bg dark:bg-dark-bg rounded-xl p-4 space-y-4">
                    <h3 className="font-semibold text-light-text dark:text-dark-text flex items-center gap-2">
                        <Mail size={18} className="text-primary" />
                        Invitation Summary
                    </h3>

                    <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                            <p className="text-light-subtle dark:text-dark-subtle">Tenant Email</p>
                            <p className="font-medium text-light-text dark:text-dark-text">{invitationData.tenantEmail}</p>
                        </div>
                        <div>
                            <p className="text-light-subtle dark:text-dark-subtle">Property</p>
                            <p className="font-medium text-light-text dark:text-dark-text">{property?.name}</p>
                        </div>
                        <div>
                            <p className="text-light-subtle dark:text-dark-subtle">Unit</p>
                            <p className="font-medium text-light-text dark:text-dark-text">{unit?.unit_number}</p>
                        </div>
                        <div>
                            <p className="text-light-subtle dark:text-dark-subtle">Start Date</p>
                            <p className="font-medium text-light-text dark:text-dark-text">
                                {invitationData.leaseStartDate ? new Date(invitationData.leaseStartDate).toLocaleDateString() : '-'}
                            </p>
                        </div>
                        <div>
                            <p className="text-light-subtle dark:text-dark-subtle">Monthly Rent</p>
                            <p className="font-medium text-primary">KES {invitationData.rentAmount?.toLocaleString()}</p>
                        </div>
                        <div>
                            <p className="text-light-subtle dark:text-dark-subtle">Security Deposit</p>
                            <p className="font-medium text-primary">KES {invitationData.securityDeposit?.toLocaleString()}</p>
                        </div>
                    </div>

                    {invitationData.houseRules && (
                        <div className="pt-4 border-t border-light-border dark:border-dark-border">
                            <p className="text-light-subtle dark:text-dark-subtle text-xs uppercase mb-2">House Rules</p>
                            <p className="text-light-text dark:text-dark-text text-sm">{invitationData.houseRules}</p>
                        </div>
                    )}
                </div>

                <div className="flex items-start gap-3 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                    <AlertCircle className="text-blue-600 dark:text-blue-400 shrink-0" size={20} />
                    <div className="text-sm text-light-text dark:text-dark-text">
                        <p className="font-medium mb-1">Important</p>
                        <p className="text-light-subtle dark:text-dark-subtle">
                            The tenant will receive an email invitation to review and accept these terms. 
                            Once accepted, a formal lease agreement will be created.
                        </p>
                    </div>
                </div>
            </div>
        );
    };

    const renderSuccessStep = () => (
        <div className="h-full flex flex-col items-center justify-center text-center space-y-6 py-12">
            <div className="w-20 h-20 bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 rounded-full flex items-center justify-center animate-in zoom-in duration-300">
                <Check size={40} />
            </div>
            <div>
                <h3 className="text-2xl font-bold text-light-text dark:text-dark-text dark:text-white mb-2">Invitation Sent!</h3>
                <p className="text-light-subtle dark:text-dark-subtle max-w-sm">
                    {successMessage}
                </p>
                <p className="text-sm text-light-subtle dark:text-dark-subtle mt-4">
                    The tenant will receive an email with instructions to complete their registration.
                </p>
            </div>
            <div className="flex items-center gap-2 text-sm text-light-subtle dark:text-dark-subtle">
                <RefreshCw size={14} className="animate-spin" />
                <span>Redirecting...</span>
            </div>
        </div>
    );

    const renderProgressIndicator = () => {
        const steps: { key: InviteStep; label: string; icon: React.ReactNode }[] = [
            { key: 'search', label: 'Tenant', icon: <Search size={14} /> },
            { key: 'details', label: 'Details', icon: <Home size={14} /> },
            { key: 'review', label: 'Review', icon: <Mail size={14} /> },
            { key: 'success', label: 'Done', icon: <Check size={14} /> },
        ];

        return (
            <div className="flex items-center justify-center gap-2">
                {steps.map((step, index) => {
                    const isActive = step.key === currentStep;
                    const isCompleted = steps.findIndex(s => s.key === currentStep) > index;

                    return (
                        <React.Fragment key={step.key}>
                            <div
                                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                                    isActive
                                        ? 'bg-primary text-white'
                                        : isCompleted
                                            ? 'bg-primary/20 text-primary'
                                            : 'bg-light-bg dark:bg-dark-bg text-light-subtle dark:text-dark-subtle'
                                }`}
                            >
                                {step.icon}
                                {step.label}
                            </div>
                            {index < steps.length - 1 && (
                                <div className={`w-4 h-0.5 ${isCompleted ? 'bg-primary' : 'bg-light-border dark:bg-dark-border'}`} />
                            )}
                        </React.Fragment>
                    );
                })}
            </div>
        );
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
            <div className="bg-light-card dark:bg-dark-card rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden flex flex-col max-h-[90vh]">
                {/* Header */}
                <div className="flex items-center justify-between p-6 bg-light-bg dark:bg-dark-bg border-b border-light-border dark:border-dark-border">
                    <div>
                        <h2 className="text-xl font-bold text-light-text dark:text-dark-text dark:text-white">
                            {currentStep === 'success' ? 'Invitation Sent' : 'Invite New Tenant'}
                        </h2>
                        {currentStep !== 'success' && currentStep !== 'search' && (
                            <p className="text-sm text-light-subtle dark:text-dark-subtle mt-1">
                                Step {['search', 'details', 'review'].indexOf(currentStep) + 1} of 3
                            </p>
                        )}
                    </div>
                    <button
                        onClick={handleClose}
                        className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
                    >
                        <X size={20} className="text-light-subtle dark:text-dark-subtle" />
                    </button>
                </div>

                {/* Progress Indicator */}
                {currentStep !== 'success' && (
                    <div className="px-6 py-3 border-b border-light-border dark:border-dark-border">
                        {renderProgressIndicator()}
                    </div>
                )}

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6">
                    {currentStep === 'success' ? (
                        renderSuccessStep()
                    ) : (
                        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                            {renderStep()}
                        </form>
                    )}
                </div>

                {/* Footer Actions */}
                {currentStep !== 'success' && (
                    <div className="flex items-center justify-end gap-4 p-6 border-t border-light-border dark:border-dark-border">
                        {currentStep !== 'search' && (
                            <button
                                type="button"
                                onClick={handlePreviousStep}
                                className="px-6 py-2.5 text-sm font-semibold text-light-text dark:text-dark-text hover:bg-light-bg dark:hover:bg-dark-bg rounded-lg transition-colors"
                            >
                                Back
                            </button>
                        )}
                        {currentStep === 'review' ? (
                            <button
                                type="button"
                                onClick={handleSubmit(onSubmit)}
                                disabled={isSubmitting || loading}
                                className="flex items-center justify-center gap-2 px-8 py-2.5 text-sm font-semibold text-white bg-primary rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                                {isSubmitting ? (
                                    <>
                                        <Loader2 size={18} className="animate-spin" />
                                        Sending...
                                    </>
                                ) : (
                                    <>
                                        <Check size={18} />
                                        Send Invitation
                                    </>
                                )}
                            </button>
                        ) : (
                            <button
                                type="button"
                                onClick={handleNextStep}
                                disabled={currentStep === 'search' && !emailValue}
                                className="flex items-center justify-center gap-2 px-8 py-2.5 text-sm font-semibold text-white bg-primary rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                                Continue
                            </button>
                        )}
                    </div>
                )}
            </div>

            {/* Cancel Confirmation Dialog */}
            {showCancelConfirm && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60">
                    <div className="bg-light-card dark:bg-dark-card rounded-xl p-6 max-w-sm w-full shadow-2xl">
                        <h3 className="text-lg font-bold text-light-text dark:text-dark-text mb-2">Discard Progress?</h3>
                        <p className="text-sm text-light-subtle dark:text-dark-subtle mb-6">
                            Are you sure you want to cancel? All entered information will be lost.
                        </p>
                        <div className="flex gap-3">
                            <button
                                onClick={() => setShowCancelConfirm(false)}
                                className="flex-1 px-4 py-2 text-sm font-medium text-light-text dark:text-dark-text border border-light-border dark:border-dark-border rounded-lg hover:bg-light-bg dark:hover:bg-dark-bg transition-colors"
                            >
                                Keep Editing
                            </button>
                            <button
                                onClick={confirmClose}
                                className="flex-1 px-4 py-2 text-sm font-medium text-white bg-danger rounded-lg hover:bg-danger/90 transition-colors"
                            >
                                Discard
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};